/**
 * Editable Structured Feedback Component
 * Allows teachers to edit scores, feedback, and summary with live total recalculation
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Card,
  CardContent,
  Chip,
  Stack,
  LinearProgress,
  Alert,
  TextField,
  IconButton,
  Tooltip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
import { CodeSearchContext } from '../../contexts/CodeSearchContext';
import { getBestSearchKeyword } from '../../utils/criterionSearchPatterns';

interface Criterion {
  key?: string;
  name: string;
  score: number;
  max_score: number;
  feedback: string;
}

interface StructuredFeedbackData {
  score: number;
  summary?: string;
  criteria?: Criterion[];
}

interface EditableStructuredFeedbackProps {
  initialData: StructuredFeedbackData | null;
  onDataChange: (data: StructuredFeedbackData) => void;
}

// Strip "Q2.1 — " or "2.1 — " prefix from a criterion name when the key chip already shows it
const stripKeyPrefix = (name: string, key?: string): string => {
  if (!key) return name;
  const escaped = key.replace(/\./g, '\\.');
  const stripped = name.replace(new RegExp(`^Q?${escaped}\\s*[—\\-–]?\\s*`, 'i'), '').trim();
  return stripped || name;
};

const EditableStructuredFeedback: React.FC<EditableStructuredFeedbackProps> = ({
  initialData,
  onDataChange
}) => {
  const [data, setData] = useState<StructuredFeedbackData | null>(initialData);
  const [editingCriterion, setEditingCriterion] = useState<number | null>(null);
  const [editingSummary, setEditingSummary] = useState(false);

  // Use the search context if available
  const searchContext = React.useContext(CodeSearchContext);

  // Handle search-icon click — mirrors Q-chip logic:
  // theory (plain integer key) → scroll Q&A to that question
  // programming (dotted key)   → keyword search across code blocks, no Q&A scroll
  const handleCriterionClick = (criterion: Criterion) => {
    if (!searchContext) return;

    if (criterion.key && /^\d+$/.test(criterion.key)) {
      // Theory: navigate Q&A to this question, clear code search
      searchContext.setSearchTerm('');
      searchContext.setHighlightedCriterion(criterion.name);
      searchContext.setQuestionFilter(parseInt(criterion.key));
    } else {
      // Programming: keyword search scoped to the question that owns this criterion.
      // "2.1" → questionFilter=2 so only Q2's CodeViewer highlights, not Q3/Q4.
      const keyword = getBestSearchKeyword(stripKeyPrefix(criterion.name, criterion.key), criterion.feedback);
      const qNum = criterion.key ? parseInt(criterion.key.split('.')[0], 10) : NaN;
      searchContext.setSearchTerm(keyword);
      searchContext.setHighlightedCriterion(criterion.name);
      searchContext.setQuestionFilter(isNaN(qNum) ? null : qNum);
    }
  };

  // Update data when initialData changes
  useEffect(() => {
    setData(initialData);
  }, [initialData]);

  // Recalculate total score whenever criteria change
  useEffect(() => {
    if (data && data.criteria) {
      const newTotal = data.criteria.reduce((sum, c) => sum + (Number(c.score) || 0), 0);
      if (newTotal !== data.score) {
        const updatedData = { ...data, score: newTotal };
        setData(updatedData);
        onDataChange(updatedData);
      }
    }
  }, [data?.criteria]);

  const handleScoreChange = (index: number, newScore: string) => {
    if (!data || !data.criteria) return;

    const score = parseFloat(newScore) || 0;
    const maxScore = data.criteria[index].max_score;

    // Validate score doesn't exceed max
    const validScore = Math.min(Math.max(0, score), maxScore);

    const updatedCriteria = [...data.criteria];
    updatedCriteria[index] = { ...updatedCriteria[index], score: validScore };

    const updatedData = { ...data, criteria: updatedCriteria };
    setData(updatedData);
    onDataChange(updatedData);
  };

  const handleFeedbackChange = (index: number, newFeedback: string) => {
    if (!data || !data.criteria) return;

    const updatedCriteria = [...data.criteria];
    updatedCriteria[index] = { ...updatedCriteria[index], feedback: newFeedback };

    const updatedData = { ...data, criteria: updatedCriteria };
    setData(updatedData);
    onDataChange(updatedData);
  };

  const handleSummaryChange = (newSummary: string) => {
    if (!data) return;

    const updatedData = { ...data, summary: newSummary };
    setData(updatedData);
    onDataChange(updatedData);
  };

  // Get color based on percentage
  const getScoreColor = (percentage: number): 'success' | 'warning' | 'error' => {
    if (percentage >= 80) return 'success';
    if (percentage >= 60) return 'warning';
    return 'error';
  };

  // Get icon based on percentage
  const getScoreIcon = (percentage: number) => {
    if (percentage >= 80) return <CheckCircleIcon fontSize="small" />;
    if (percentage >= 60) return <WarningIcon fontSize="small" />;
    return <ErrorIcon fontSize="small" />;
  };

  if (!data || !data.criteria) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="body2" color="textSecondary">
          No structured data available. Click "Copy from AI" to use AI feedback as a starting point.
        </Typography>
      </Paper>
    );
  }

  const totalMaxScore = data.criteria.reduce((sum, c) => sum + c.max_score, 0);
  const overallPercentage = totalMaxScore > 0 ? (data.score / totalMaxScore) * 100 : 0;

  return (
    <Box>
      {/* Overall Summary */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="body2" fontWeight="bold">
            Overall Assessment
          </Typography>
          <IconButton
            size="small"
            onClick={() => setEditingSummary(!editingSummary)}
            color={editingSummary ? 'primary' : 'default'}
          >
            {editingSummary ? <CheckIcon /> : <EditIcon />}
          </IconButton>
        </Stack>
        {editingSummary ? (
          <TextField
            fullWidth
            multiline
            rows={2}
            value={data.summary || ''}
            onChange={(e) => handleSummaryChange(e.target.value)}
            variant="outlined"
            size="small"
            sx={{ bgcolor: 'background.paper' }}
          />
        ) : (
          <Typography variant="body2">
            {data.summary || 'No summary provided'}
          </Typography>
        )}
      </Alert>

      {/* Grand Total Score */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.50', border: 1, borderColor: 'primary.main' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" color="primary">
            Grand Total
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            {getScoreIcon(overallPercentage)}
            <Typography variant="h5" fontWeight="bold" color="primary">
              {data.score} / {totalMaxScore}
            </Typography>
            <Chip
              label={`${overallPercentage.toFixed(0)}%`}
              color={getScoreColor(overallPercentage)}
              size="small"
            />
          </Box>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={overallPercentage}
          sx={{ mt: 1, height: 8, borderRadius: 4 }}
          color={getScoreColor(overallPercentage)}
        />
      </Paper>

      {/* Individual Criteria */}
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Detailed Feedback by Criterion
      </Typography>

      <Stack spacing={2}>
        {data.criteria.map((criterion, index) => {
          const percentage = criterion.max_score > 0 ? (criterion.score / criterion.max_score) * 100 : 0;
          const color = getScoreColor(percentage);
          const isEditing = editingCriterion === index;

          return (
            <Card key={index} variant="outlined">
              <CardContent>
                {/* Header Row: Q-number chip (clickable) + criterion name */}
                <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
                  {criterion.key && (
                    <Tooltip title="Go to question in Q&A" placement="top">
                      <Chip
                        label={`Q${criterion.key}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ flexShrink: 0, cursor: searchContext ? 'pointer' : 'default', '&:hover': searchContext ? { bgcolor: 'primary.main', color: 'white' } : {} }}
                        onClick={() => {
                          if (!searchContext) return;
                          if (/^\d+$/.test(criterion.key!)) {
                            // Theory: scroll directly to matching Q&A question number
                            searchContext.setSearchTerm('');
                            searchContext.setQuestionFilter(parseInt(criterion.key!));
                          } else {
                            // Programming: scope to the owning question's CodeViewer
                            const keyword = getBestSearchKeyword(stripKeyPrefix(criterion.name, criterion.key), criterion.feedback);
                            const qNum = criterion.key ? parseInt(criterion.key.split('.')[0], 10) : NaN;
                            searchContext.setSearchTerm(keyword);
                            searchContext.setQuestionFilter(isNaN(qNum) ? null : qNum);
                          }
                        }}
                      />
                    </Tooltip>
                  )}
                  <Typography variant="subtitle2" fontWeight="bold">
                    {stripKeyPrefix(criterion.name, criterion.key)}
                  </Typography>
                </Box>

                {/* Score Row: score / max — % chip — search & edit icons */}
                <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                  {getScoreIcon(percentage)}
                  <Box display="flex" alignItems="center" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                    <TextField
                      type="number"
                      value={criterion.score}
                      onChange={(e) => handleScoreChange(index, e.target.value)}
                      inputProps={{
                        min: 0,
                        max: criterion.max_score,
                        step: 1,
                        style: { textAlign: 'right', width: '60px', fontWeight: 'bold' }
                      }}
                      size="small"
                      variant="standard"
                      sx={{ width: '80px' }}
                    />
                    <Typography variant="h6" fontWeight="bold" color={`${color}.main`} sx={{ ml: 0.5 }}>
                      / {criterion.max_score}
                    </Typography>
                  </Box>
                  <Chip
                    label={`${percentage.toFixed(0)}%`}
                    color={color}
                    size="small"
                  />
                  {searchContext && (
                    <Tooltip title={`Search code for: ${getBestSearchKeyword(criterion.name, criterion.feedback)}`}>
                      <IconButton
                        size="small"
                        onClick={() => handleCriterionClick(criterion)}
                        color={searchContext.highlightedCriterion === criterion.name ? 'primary' : 'default'}
                        sx={{
                          ...(searchContext.highlightedCriterion === criterion.name && {
                            bgcolor: 'primary.main',
                            color: 'white',
                            '&:hover': { bgcolor: 'primary.dark' },
                          }),
                        }}
                      >
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => setEditingCriterion(isEditing ? null : index)}
                    color={isEditing ? 'primary' : 'default'}
                  >
                    {isEditing ? <CheckIcon /> : <EditIcon />}
                  </IconButton>
                </Box>

                <LinearProgress
                  variant="determinate"
                  value={percentage}
                  sx={{ mb: 2, height: 6, borderRadius: 3 }}
                  color={color}
                />

                {isEditing ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={criterion.feedback}
                    onChange={(e) => handleFeedbackChange(index, e.target.value)}
                    variant="outlined"
                    size="small"
                    placeholder="Enter feedback for this criterion..."
                  />
                ) : (
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                    <Typography variant="body2">
                      {criterion.feedback || 'No feedback provided'}
                    </Typography>
                  </Paper>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      {/* Summary Table */}
      <Paper sx={{ p: 2, mt: 3, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Score Breakdown
        </Typography>
        <Divider sx={{ my: 1 }} />
        <Stack spacing={0.5}>
          {data.criteria.map((criterion, index) => (
            <Box
              key={index}
              display="flex"
              justifyContent="space-between"
              sx={{ py: 0.5 }}
            >
              <Typography variant="body2">
                {criterion.key ? `Q${criterion.key} — ${stripKeyPrefix(criterion.name, criterion.key)}` : criterion.name}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {criterion.score} / {criterion.max_score}
              </Typography>
            </Box>
          ))}
          <Divider sx={{ my: 1 }} />
          <Box display="flex" justifyContent="space-between" sx={{ py: 0.5 }}>
            <Typography variant="body1" fontWeight="bold">
              Total
            </Typography>
            <Typography variant="body1" fontWeight="bold" color="primary">
              {data.score} / {totalMaxScore}
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default EditableStructuredFeedback;
